import {
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
} from '@ads/components-react';
import {
  EllipsisVerticalIcon,
  LanguageIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieVideoGridItemViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface SerieVideoTableProps {
  readonly data: readonly SerieVideoGridItemViewModel[];
  readonly isLoading: boolean;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (video: SerieVideoGridItemViewModel) => void;
  readonly onManageLangs: (video: SerieVideoGridItemViewModel) => void;
  readonly onDelete: (video: SerieVideoGridItemViewModel) => void;
}

/**
 * Tabela de Vídeos de uma Série. Colunas Título, Data, Ativo (Tag) e um menu de
 * ações "..." agrupando Editar/Idiomas (ícone `languages`, mesmo padrão da
 * ação "Idiomas" de Série e de Países) e, após um divisor, Excluir. Fiel ao
 * frame *Vídeo · Listagem* de `design/series.pen`.
 */
export function SerieVideoTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onManageLangs,
  onDelete,
}: SerieVideoTableProps) {
  const { t } = useTranslation('series');

  const columns = useMemo<ColumnDef<SerieVideoGridItemViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'langName',
        header: t('videos.table.columns.title'),
        cell: info => (
          <span className="ts-subtitle-xxs text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        accessorKey: 'date',
        header: t('videos.table.columns.date'),
        cell: info => {
          const value = info.getValue() as string | undefined;
          return (
            <span className="ts-paragraph-xxs">
              {value ? dayjs(value.slice(0, 10)).format('DD/MM/YYYY') : ''}
            </span>
          );
        },
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: t('videos.table.columns.active'),
        cell: ({ row }) => {
          const active = row.original.active ?? false;
          return (
            <Tag
              label={t(active ? 'videos.table.statusActive' : 'videos.table.statusInactive')}
              size="md"
              status={active ? 'positive' : 'default'}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('videos.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <Dropdown
              trigger={
                <IconButton
                  icon={EllipsisVerticalIcon}
                  ariaLabel={t('videos.table.columns.actions')}
                  hierarchy="tertiary"
                  size="md"
                />
              }
            >
              <DropdownItem onSelect={() => onEdit(row.original)}>
                <DropdownItemContent leadingIcon={PencilSquareIcon}>
                  {t('videos.table.menu.edit')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem onSelect={() => onManageLangs(row.original)}>
                <DropdownItemContent leadingIcon={LanguageIcon}>
                  {t('videos.table.menu.langs')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem status="destructive" onSelect={() => onDelete(row.original)}>
                <DropdownItemContent leadingIcon={TrashIcon}>
                  {t('videos.table.menu.delete')}
                </DropdownItemContent>
              </DropdownItem>
            </Dropdown>
          </div>
        ),
      },
    ],
    [t, onEdit, onManageLangs, onDelete],
  );

  return (
    <DataTable
      data={data as SerieVideoGridItemViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('videos.table.empty')}
      emptyStateDescription={t('videos.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
    />
  );
}
